export type EventResponseKind = 'going' | 'interested';

export type EventFriendPreview = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
};

export type EventCity = {
  id: string | null;
  name: string | null;
  slug: string | null;
};

export type EventVenue = {
  id: string | null;
  name: string | null;
  timezone: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type EventSummary = {
  id: string;
  title: string;
  slug: string | null;
  cover_url: string | null;
  venue: EventVenue;
  city: EventCity;
  city_id: string;
  starts_at: string | null;
  ends_at: string | null;
  capacity: number | null;
  going_count: number;
  interested_count: number;
  is_full: boolean;
  friends_going_count: number;
  friends_going_preview: EventFriendPreview[];
};

export type EventDetail = EventSummary & {
  description: string | null;
  my_response: EventResponseKind | null;
  is_published: boolean;
  published_at: string | null;
};

export type EventsListResponse = {
  data: EventSummary[];
  meta: {
    next_cursor: string | null;
    has_more: boolean;
  };
};

export type EventDetailResponse = {
  event: EventDetail;
};

export type EventResponseStatus = {
  my_response: EventResponseKind | null;
  going_count: number;
  interested_count: number;
  is_full: boolean;
};

export type EventsSessionResponse = {
  authenticated: boolean;
  user: null | {
    id: string;
    display_name: string | null;
    avatar_url?: string | null;
  };
};

export class EventsApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly payload: unknown;

  constructor(status: number, code: string | null, message: string, payload: unknown) {
    super(message);
    this.name = 'EventsApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

let csrfToken: string | null = null;

async function readPayload(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('application/json') ? response.json() : null;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
  }

  const response = await fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const payload = await readPayload(response);

  if (!response.ok) {
    const code = typeof payload?.error === 'string'
      ? payload.error
      : typeof payload?.error?.code === 'string'
        ? payload.error.code
        : null;
    const message = typeof payload?.message === 'string'
      ? payload.message
      : typeof payload?.error?.message === 'string'
        ? payload.error.message
        : 'Не удалось выполнить запрос.';
    throw new EventsApiError(response.status, code, message, payload);
  }

  return payload as T;
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const payload = await readPayload(response) as { csrf_token?: string } | null;

  if (!response.ok || !payload?.csrf_token) {
    throw new EventsApiError(response.status, null, 'Не удалось подготовить защищённый запрос.', payload);
  }

  csrfToken = payload.csrf_token;
  return csrfToken;
}

export function fetchEvents(when: 'today' | 'week' | 'upcoming', cursor: string | null = null, city: string | null = null): Promise<EventsListResponse> {
  const params = new URLSearchParams({ when, limit: '20' });
  if (cursor) params.set('cursor', cursor);
  if (city) params.set('city', city);
  return request<EventsListResponse>(`/api/v1/events?${params.toString()}`);
}

export function fetchVenueEvents(
  venueId: string,
  cursor: string | null = null,
  limit = 3,
  signal?: AbortSignal,
): Promise<EventsListResponse> {
  const params = new URLSearchParams({
    venue: venueId,
    when: 'upcoming',
    limit: String(limit),
  });
  if (cursor) params.set('cursor', cursor);
  return request<EventsListResponse>(`/api/v1/events?${params.toString()}`, { signal });
}

export function fetchEvent(eventId: string): Promise<EventDetailResponse> {
  return request<EventDetailResponse>(`/api/v1/events/${encodeURIComponent(eventId)}`);
}

export function fetchEventsSession(): Promise<EventsSessionResponse> {
  return request<EventsSessionResponse>('/api/v1/auth/session');
}

export function respondToEvent(eventId: string, responseType: EventResponseKind): Promise<EventResponseStatus> {
  return request<EventResponseStatus>(`/api/v1/events/${encodeURIComponent(eventId)}/respond`, {
    method: 'POST',
    body: JSON.stringify({ response_type: responseType }),
  });
}

export function cancelEventResponse(eventId: string): Promise<EventResponseStatus> {
  return request<EventResponseStatus>(`/api/v1/events/${encodeURIComponent(eventId)}/respond`, {
    method: 'DELETE',
  });
}
